import { Stripe, StripeElements } from '@stripe/stripe-js';

interface IConfirmJobPaymentParams {
  stripe: Stripe | null;
  elements: StripeElements | null;
  clientSecret: string;
}

export interface IConfirmJobPaymentResult {
  paymentIntentId?: string;
  error?: string;
}

/**
 * Confirms the PaymentIntent for the current step's <Elements>.
 *
 * `redirect: 'if_required'` keeps card/ACH/Link flows in-page (no full redirect),
 * so on success we get the PaymentIntent back here and return its id for finalize.
 *
 * Recovery: a double-click, a lost network response, or an ACH intent that has
 * already advanced can make confirmPayment return `payment_intent_unexpected_state`.
 * In that case we retrieve the PI and, if it's already `processing` or `succeeded`,
 * return its id anyway so finalize can proceed instead of failing spuriously.
 */
export const confirmJobPayment = async ({
  stripe,
  elements,
  clientSecret,
}: IConfirmJobPaymentParams): Promise<IConfirmJobPaymentResult> => {
  if (!stripe || !elements) {
    return { error: 'Payment is not ready yet. Please try again.' };
  }

  const result = await stripe.confirmPayment({
    elements,
    redirect: 'if_required',
    confirmParams: {
      return_url: window.location.href,
    },
  });

  if (result.error) {
    if (result.error.code === 'payment_intent_unexpected_state') {
      const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
      if (paymentIntent && (paymentIntent.status === 'processing' || paymentIntent.status === 'succeeded')) {
        return { paymentIntentId: paymentIntent.id };
      }
    }
    return { error: result.error.message || 'Payment failed. Please try again.' };
  }

  if (result.paymentIntent) {
    return { paymentIntentId: result.paymentIntent.id };
  }

  return { error: 'Payment could not be confirmed. Please try again.' };
};

export default confirmJobPayment;
